// pages/login.ts — split-screen sign-in page (also serves signup via mode).
//
// Behaviour mirrors frontend2/components/Auth.jsx :: LoginPage:
//   • "Continue with Google" → loginWithGoogle()
//   • Demo-account buttons   → loginWithToken(token)
//   • Email form             → loginWithEmail(email)
//
// Demo panel + email form are gated on window.auth.AUTH_MODE.shim.
// In prod (shim=false) only the Google button is shown.

import type { Page, PageContext } from '../router';
import { html, esc, arrowR } from '../util';
import { loginWithGoogle, loginWithEmail, loginWithToken } from '../auth';

function googleIcon(size = 18): string {
  return html`
    <svg width="${size}" height="${size}" viewBox="0 0 24 24" style="flex-shrink: 0;" aria-hidden="true">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
    </svg>
  `;
}

export function makeLoginPage(mode: 'signin' | 'signup'): Page {
  const isSignup = mode === 'signup';

  return {
    fullScreen: true,

    render(_ctx: PageContext): string {
      const heading  = isSignup ? 'Create your account.' : 'Welcome back.';
      const subline  = isSignup
        ? html`Already have one? <a class="bff-auth-link" data-nav="login">Sign in</a>.`
        : html`New here? <a class="bff-auth-link" data-nav="signup">Start an account</a> — it takes about a minute.`;
      const googleLabel = isSignup ? 'Sign up with Google' : 'Continue with Google';
      const footnote = isSignup
        ? 'By creating an account, you agree to use this service for grant applications only. New accounts are granted the applicant role by default.'
        : "We don't keep cookies for anything other than your signed-in session.";

      return html`
        <div class="bff-login">
          <!-- LEFT — calm marketing panel -->
          <div class="bff-login-left">
            <div>
              <a class="bff-login-eyebrow" data-nav="landing">
                <span class="eyebrow">The Bray Family Foundation</span>
              </a>
              <h1 class="t-h1 bff-login-title" style="margin: 24px 0 0;">
                From dormancy to renewal.
              </h1>
              <p class="t-lede" style="max-width: 520px; margin-top: 32px;">
                Every great change begins with a single seed — and a small foundation willing to write the first cheque.
              </p>
            </div>
            <img src="assets/img/login-marketing.jpg"
                 alt="A gardener turning over fresh soil to plant in early spring"
                 class="brand-img"
                 style="aspect-ratio: 5/3; width: 100%; max-width: 520px; margin-top: 48px;"/>
          </div>

          <!-- RIGHT — auth panel -->
          <div class="bff-login-right">
            <div class="bff-login-card">
              <h2 class="t-h2 bff-login-heading" style="margin: 0;">${heading}</h2>
              <p class="t-body" style="margin-top: 16px;">${{ __raw: subline }}</p>

              <button type="button" class="btn btn-primary bff-login-google"
                      data-action="google" style="margin-top: 48px;">
                ${{ __raw: googleIcon(18) }}
                <span data-google-label>${googleLabel}</span>
              </button>

              <div id="bff-login-error" class="bff-login-error" hidden></div>

              <!-- Demo panel + email form (shim-only, hidden by default). -->
              <div id="bff-login-shim" hidden>
                <div class="bff-login-divider">
                  <span class="t-small">or use a demo account</span>
                </div>

                <div class="card" style="padding: 14px; margin-top: 24px;">
                  <div class="label" style="font-size: 11.5px; margin-bottom: 16px;">Demo accounts</div>
                  <div id="bff-demo-accounts" class="bff-demo-grid"></div>
                </div>

                ${{ __raw: isSignup ? '' : html`
                <form id="bff-login-form" class="d-flex flex-column" style="gap: 16px; margin-top: 32px;">
                  <div class="bff-field">
                    <label class="form-label">Email</label>
                    <input class="form-control" type="email" name="email"
                           placeholder="you@organisation.org" required/>
                  </div>
                  <div class="bff-field">
                    <label class="form-label">Password
                      <span class="t-small">(any value works for demo accounts)</span>
                    </label>
                    <input class="form-control" type="password" name="password" placeholder="••••••••"/>
                  </div>
                  <a class="bff-auth-link" data-nav="forgot-password"
                     style="font-size: 14px; font-weight: 500; margin-top: -4px;">Forgot password?</a>
                  <button type="submit" class="btn btn-primary"
                          style="width: 100%; padding: 16px; justify-content: center; margin-top: 16px;">
                    <span data-submit-label>Sign in with email</span> ${{ __raw: arrowR() }}
                  </button>
                </form>
                ` }}
              </div>

              <p class="t-small" style="margin-top: 32px; max-width: 380px; line-height: 1.6;">
                ${footnote}
              </p>
            </div>
          </div>
        </div>
      `;
    },

    mount(root: HTMLElement, _ctx: PageContext): void {
      let alive = true;
      let busy = false;

      const errorEl   = root.querySelector('#bff-login-error')      as HTMLElement | null;
      const shimEl    = root.querySelector('#bff-login-shim')       as HTMLElement | null;
      const acctsEl   = root.querySelector('#bff-demo-accounts')    as HTMLElement | null;
      const googleBtn = root.querySelector('[data-action="google"]') as HTMLButtonElement | null;
      const googleLbl = root.querySelector('[data-google-label]')   as HTMLElement | null;
      const submitLbl = root.querySelector('[data-submit-label]')   as HTMLElement | null;
      const formEl    = root.querySelector('#bff-login-form')       as HTMLFormElement | null;

      const initialGoogleLabel = googleLbl ? googleLbl.textContent || '' : '';
      const initialSubmitLabel = submitLbl ? submitLbl.textContent || '' : '';

      function showError(msg: string): void {
        if (!errorEl) return;
        errorEl.textContent = msg;
        errorEl.hidden = !msg;
      }
      function setBusy(b: boolean): void {
        busy = b;
        if (googleBtn) googleBtn.disabled = b;
        if (googleLbl) googleLbl.textContent = b ? 'Signing in…' : initialGoogleLabel;
        if (submitLbl) submitLbl.textContent = b ? 'Signing in…' : initialSubmitLabel;
        if (formEl) {
          formEl.querySelectorAll('input, button').forEach(el => {
            (el as HTMLInputElement | HTMLButtonElement).disabled = b;
          });
        }
      }

      function renderDemoAccounts(): void {
        if (!acctsEl) return;
        const accounts = window.auth.DEMO_ACCOUNTS;
        if (!accounts.length) {
          acctsEl.innerHTML = `
            <div class="t-small" style="grid-column: 1 / -1;">
              No demo accounts available — check that the API is running and has seeded users.
            </div>`;
          return;
        }
        acctsEl.innerHTML = accounts.map(a => {
          // `name` is on the demo-account row; tolerate older shapes.
          const display = (a as { name?: string; fullName?: string }).name
                      || (a as { name?: string; fullName?: string }).fullName
                      || a.email;
          return `
            <button type="button" class="bff-demo-btn" data-token="${esc(a.token)}">
              <div class="bff-demo-name">${esc(display)}</div>
              <div class="bff-demo-role">${esc(a.role || '')}</div>
            </button>
          `;
        }).join('');
      }

      // Reveal demo / shim UI once we know AUTH_MODE.
      Promise.all([
        window.auth.AUTH_MODE_READY,
        window.auth.DEMO_READY,
      ]).then(() => {
        if (!alive) return;
        if (window.auth.AUTH_MODE.shim) {
          if (shimEl) shimEl.hidden = false;
          renderDemoAccounts();
        }
      }).catch(() => { /* boot best-effort; stay in prod-safe UI */ });

      async function doLogin(promise: Promise<{ Role?: string; role?: string }>): Promise<void> {
        showError('');
        setBusy(true);
        try {
          // On success, window.auth's listener fires synchronously and
          // main.ts redirects to the dashboard — this page is unmounted
          // out from under us, so there's nothing to do here.
          await promise;
        } catch (e) {
          if (!alive) return;
          const msg = (e && (e as { message?: string }).message) || 'Sign in failed.';
          showError(msg);
        } finally {
          if (alive) setBusy(false);
        }
      }

      // Delegated handlers for Google button and demo-account picker.
      root.addEventListener('click', (e) => {
        const t = e.target as HTMLElement | null;
        const googleEl = t?.closest('[data-action="google"]') as HTMLElement | null;
        if (googleEl) {
          e.preventDefault();
          if (busy) return;
          void doLogin(loginWithGoogle());
          return;
        }
        const demoEl = t?.closest('[data-token]') as HTMLElement | null;
        if (demoEl) {
          e.preventDefault();
          if (busy) return;
          const token = demoEl.getAttribute('data-token') || '';
          void doLogin(loginWithToken(token));
        }
      });

      // Email form submit.
      formEl?.addEventListener('submit', (e) => {
        e.preventDefault();
        if (busy) return;
        const data = new FormData(formEl);
        const email = String(data.get('email') || '').trim();
        if (!email) { showError('Please enter your email.'); return; }
        void doLogin(loginWithEmail(email));
      });

      // Stash a cleanup hook so unmount() can stop in-flight calls.
      const self = (makeLoginPage as unknown) as { _alive?: () => void };
      self._alive = () => { alive = false; };
    },

    unmount(): void {
      const self = (makeLoginPage as unknown) as { _alive?: () => void };
      if (self._alive) self._alive();
    },
  };
}
